import { mulberry32 } from '../track/rng';
import type { RunResult } from './session';
import { medalFor } from './medals';

/**
 * Three seeded challenges a day, the same for everyone.
 *
 * The lap time already rewards raw pace; these reward the things pace alone
 * hides — not locking up, hitting apexes, staying on the island, being
 * consistent across sectors. They give a slow driver something to win on a day
 * the leaderboard is out of reach, which is most days.
 *
 * Every one is computed from data the run already produces. Nothing here needed
 * new recording.
 */

export interface Objective {
  id: string;
  name: string;
  detail: string;
  /** Evaluated against a completed run. */
  test: (r: RunResult) => boolean;
}

const POOL: Objective[] = [
  {
    id: 'clean',
    name: 'Clean sheet',
    detail: 'Complete the lap without putting a wheel on the grass',
    test: (r) => r.valid && !r.deltas.some((d) => d.wentOff),
  },
  {
    id: 'no-lockup',
    name: 'Smooth hands',
    detail: 'Complete the lap without locking the rears',
    test: (r) => r.valid && !r.deltas.some((d) => d.lockedUp),
  },
  {
    id: 'apex',
    name: 'On the apex',
    detail: 'Within 5 km/h of the target apex speed at every corner',
    test: (r) =>
      r.valid && r.deltas.every((d) => Math.abs(d.vMinIdeal - d.vMin) * 3.6 <= 5),
  },
  {
    id: 'late-brake',
    name: 'Brave on the brakes',
    detail: 'Brake within 10m of the ideal point at three or more corners',
    test: (r) => r.valid && r.deltas.filter((d) => Math.abs(d.brakeOffset) <= 10).length >= 3,
  },
  {
    id: 'consistent',
    name: 'Metronome',
    detail: 'No corner costs you more than 0.6s',
    test: (r) => r.valid && r.deltas.every((d) => d.lost <= 0.6),
  },
  {
    id: 'gold',
    name: 'Gold standard',
    detail: 'Earn a gold medal or better',
    test: (r) => r.valid && ['gold', 'solver'].includes(medalFor(r.timeMs, r.idealMs).id),
  },
  {
    id: 'sector-sweep',
    name: 'Sector sweep',
    detail: 'Every sector within 8% of the fastest one you drive',
    test: (r) => {
      if (!r.valid || r.sectorMs.length < 3) return false;
      const splits = r.sectorMs.map((t, i) => t - (i > 0 ? r.sectorMs[i - 1] : 0));
      // Compared against the ideal share of the lap, since sectors are equal
      // distance but never equal time.
      const shares = splits.map((s) => s / r.timeMs);
      const best = Math.min(...shares);
      return shares.every((s) => s <= best * 1.08);
    },
  },
  {
    id: 'top-speed',
    name: 'Full send',
    detail: 'Break 100 km/h somewhere on the lap',
    test: (r) => r.valid && r.maxSpeed * 3.6 >= 100,
  },
];

/** The day's three, drawn without replacement from a date-seeded shuffle. */
export function objectivesFor(seed: number): Objective[] {
  const rng = mulberry32(seed ^ 0x0b1ec7);
  const pool = [...POOL];
  // Fisher-Yates, so the draw is uniform and reproducible from the seed alone.
  for (let i = pool.length - 1; i > 0; i--) {
    const j = Math.floor(rng.next() * (i + 1));
    [pool[i], pool[j]] = [pool[j], pool[i]];
  }
  return pool.slice(0, 3);
}

export interface ObjectiveResult {
  objective: Objective;
  done: boolean;
}

export function evaluateObjectives(objectives: Objective[], r: RunResult): ObjectiveResult[] {
  return objectives.map((objective) => ({ objective, done: objective.test(r) }));
}
